import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useStore } from '@/lib/store-context';
import { getPurchaseDetail } from '@/lib/purchases-api';
import { fmtINR, fmtDate } from '@/lib/format';

export const Route = createFileRoute('/_app/purchases/bill/$storeCode/$billDate/$billNo')({
  component: Page,
});

function Page() {
  const { storeCode, billDate, billNo } = Route.useParams();
  const { selected: store } = useStore();

  const q = useQuery({
    queryKey: ['purchases:detail', storeCode, billDate, billNo, store],
    queryFn: () => getPurchaseDetail({ storeCode, billDate, billNo, store }),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          {q.data && (
            <>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Daleep · {q.data.kind === 'return' ? 'Purchase return' : 'Purchase'}
              </p>
              <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
                {q.data.kind === 'return' ? 'Return' : 'Bill'} {q.data.header.billNo}
                {q.data.kind === 'return' && (
                  <span className="ml-3 rounded bg-rose-soft px-2 py-0.5 align-middle text-xs font-medium text-rose-deep">RETURN</span>
                )}
              </h1>
            </>
          )}
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/purchases" search={(prev) => prev}>← Bills</Link>
        </Button>
      </div>

      {q.isLoading && <p>Loading…</p>}
      {q.error && (q.error as Error).message === 'NOT_FOUND' && (
        <Card><CardContent className="pt-4"><p className="text-sm text-muted-foreground">Bill not found.</p></CardContent></Card>
      )}
      {q.error && (q.error as Error).message !== 'NOT_FOUND' && (
        <p className="text-sm text-destructive">{(q.error as Error).message}</p>
      )}

      {q.data && (
        <>
          <Card>
            <CardHeader><CardTitle>Header</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Date</dt><dd>{fmtDate(q.data.header.billDate)}</dd></div>
                <div><dt className="text-muted-foreground">Store</dt><dd>{q.data.header.storeCode}</dd></div>
                <div><dt className="text-muted-foreground">Vendor bill (ref)</dt><dd className="font-mono">{q.data.header.refBillNo ?? '—'}</dd></div>
                <div><dt className="text-muted-foreground">Entered by</dt><dd>{q.data.header.enteredBy ?? '—'}</dd></div>
                <div><dt className="text-muted-foreground">Tender</dt><dd>{q.data.header.tender ?? '—'}</dd></div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Payment</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div><dt className="text-muted-foreground">Cash</dt><dd>{fmtINR(q.data.payment.cashAmt)}</dd></div>
                <div><dt className="text-muted-foreground">Card</dt><dd>{fmtINR(q.data.payment.cardAmt)}</dd></div>
                <div><dt className="text-muted-foreground">Net amt</dt><dd>{fmtINR(q.data.totals.netAmt)}</dd></div>
                {q.data.totals.fDscntAmt > 0 && <div><dt className="text-muted-foreground">Bill discount</dt><dd>{fmtINR(q.data.totals.fDscntAmt)}</dd></div>}
                {q.data.totals.mdAmt > 0 && <div><dt className="text-muted-foreground">MD discount</dt><dd>{fmtINR(q.data.totals.mdAmt)}</dd></div>}
                {q.data.totals.dscntAmt > 0 && <div><dt className="text-muted-foreground">Discount</dt><dd>{fmtINR(q.data.totals.dscntAmt)}</dd></div>}
                {q.data.totals.freight > 0 && <div><dt className="text-muted-foreground">Freight</dt><dd>{fmtINR(q.data.totals.freight)}</dd></div>}
                {(q.data.totals.vat1 + q.data.totals.vat2 + q.data.totals.vat3) > 0 && (
                  <div>
                    <dt className="text-muted-foreground">Tax</dt>
                    <dd>{fmtINR(q.data.totals.vat1 + q.data.totals.vat2 + q.data.totals.vat3)}</dd>
                  </div>
                )}
                <div className="col-span-2 sm:col-span-4">
                  <dt className="text-muted-foreground">Bill total</dt>
                  <dd className="text-lg font-semibold">{fmtINR(q.data.totals.billAmt)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Line items ({q.data.lines.length})</CardTitle></CardHeader>
            <CardContent>
              {q.data.lines.length === 0 ? (
                <p className="text-sm text-muted-foreground">No line items.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Item</TH>
                      <TH>Item code</TH>
                      <TH>Article</TH>
                      <TH>Color</TH>
                      <TH>Size</TH>
                      <TH>Sticker</TH>
                      <TH className="text-right">Qty</TH>
                      <TH className="text-right">PP</TH>
                      <TH className="text-right">MRP</TH>
                      <TH className="text-right">Net</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {q.data.lines.map((l, i) => (
                      <TR key={`${l.rowrollNo}-${i}`}>
                        <TD>{l.itemName}</TD>
                        <TD className="font-mono text-xs">{l.itemCode || '—'}</TD>
                        <TD>{l.style ?? '—'}</TD>
                        <TD>{l.color ?? '—'}</TD>
                        <TD>{l.size ?? '—'}</TD>
                        <TD className="font-mono text-xs">{l.rowrollNo}</TD>
                        <TD className="text-right">{l.qty}</TD>
                        <TD className="text-right">{l.pp ? fmtINR(l.pp) : '—'}</TD>
                        <TD className="text-right">{fmtINR(l.mrp)}</TD>
                        <TD className="text-right font-medium">{fmtINR(l.netAmt)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
